import SuiteEnum from 'redpoints-model-js-common/enumeration/SuiteEnum';
import AssetFilterEnum from 'redpoints-model-js/enumeration/AssetFilterEnum';
import DomainTypeEnum from 'redpoints-model-js/enumeration/DomainTypeEnum';
import OperationsEnum from 'redpoints-model-js/enumeration/OperationsEnum';
import AssetsRequestFilter from 'redpoints-model-js/model/filter/AssetsRequestFilter';

import HttpStatus from 'redpoints-connector-common/enumeration/HttpStatus';
import AuthorizationController from 'auth-restapi-connector/controller/AuthorizationController';
import AssetController from 'rpc-restapi-connector/controller/AssetController';
import ContractController from 'rpc-restapi-connector/controller/ContractController';
import DeindexationController from 'redpoints-restapi-connector/controller/DeindexationController';
import DetectionLinkWatcherController from 'redpoints-restapi-connector/controller/DetectionLinkWatcherController';
import EnforcementStatusGroupController from 'redpoints-restapi-connector/controller/EnforcementStatusGroupController';
import IncidentInfoController from 'redpoints-restapi-connector/controller/IncidentInfoController';
import IncidentInfoExportController from 'redpoints-restapi-connector/controller/IncidentInfoExportController';
import IncidentStatusGroupController from 'redpoints-restapi-connector/controller/IncidentStatusGroupController';
import ContractDomainController from 'dms-restapi-connector/controller/domain/ContractDomainController';
import ReasonLiteController from 'dms-restapi-connector/controller/reasons/ReasonLiteController';

import {
  getIncidentInfoFilters,
  paginationToPageable,
} from 'redpoints-front-common/helpers';

import {
  ALL_INCIDENTS_CLEAR,
  ALL_INCIDENTS_DATA_CLEAR,
  ALL_INCIDENTS_FETCH_DATA,
  ALL_INCIDENTS_FILTERS_CLEAR_DOMAINS,
  ALL_INCIDENTS_FILTERS_CLEAR_FILTERS,
  ALL_INCIDENTS_FILTERS_FETCH_ASSETS,
  ALL_INCIDENTS_FILTERS_FETCH_DOMAINS,
  ALL_INCIDENTS_FILTERS_FETCH_INCIDENTS_STATUS,
  ALL_INCIDENTS_FILTERS_FETCH_LINKS_STATUS,
  ALL_INCIDENTS_FILTERS_FETCH_NON_ENFORCEMENT_REASONS,
  ALL_INCIDENTS_FILTERS_FETCH_PROTECTION_TYPES,
  ALL_INCIDENTS_FILTERS_SET_APPLIED_FILTERS,
  ALL_INCIDENTS_FILTERS_SET_ASSET,
  ALL_INCIDENTS_FILTERS_SET_BOOKMARK,
  ALL_INCIDENTS_FILTERS_SET_CLOSURE_DATE,
  ALL_INCIDENTS_FILTERS_SET_CONFIRMATION_DATE,
  ALL_INCIDENTS_FILTERS_SET_DEINDEXATION,
  ALL_INCIDENTS_FILTERS_SET_DETECTION_DATE,
  ALL_INCIDENTS_FILTERS_SET_DOMAINS,
  ALL_INCIDENTS_FILTERS_SET_ENFORCEMENT_DATE,
  ALL_INCIDENTS_FILTERS_SET_ENFORCEMENT_STATUS,
  ALL_INCIDENTS_FILTERS_SET_ENFORCEMENT_STATUS_GROUP,
  ALL_INCIDENTS_FILTERS_SET_INCIDENT_STATUS,
  ALL_INCIDENTS_FILTERS_SET_INCIDENT_STATUS_GROUP,
  ALL_INCIDENTS_FILTERS_SET_PRIORITY_ID,
  ALL_INCIDENTS_FILTERS_SET_PROTECTION_TYPE,
  ALL_INCIDENTS_FILTERS_SET_REASON_CODES,
  ALL_INCIDENTS_FILTERS_SET_SEARCH_LINK_URL,
  ALL_INCIDENTS_FILTERS_SET_SEARCH_TERM,
  ALL_INCIDENTS_TABLE_ACTION,
  ALL_INCIDENTS_TABLE_EXPORT_ACTION,
  ALL_INCIDENTS_TABLE_EXPORT_BRAND_DATA_ACTION,
  ALL_INCIDENTS_TABLE_EXPORT_BRAND_PERMISSION,
  ALL_INCIDENTS_TABLE_EXPORT_PIRACY_DATA_ACTION,
  ALL_INCIDENTS_TABLE_EXPORT_PIRACY_PERMISSION,
  ALL_INCIDENTS_TABLE_FETCH_DEINDEXATION_BY_LINK,
  CONNECTOR_REQUEST,
} from './actionTypes';
import { translate } from '../helpers/languageHelper';
import { ALL_INCIDENTS_EXPORT_ENTITY_PERMISSION } from '../pages/Reporting/AllIncidents/constants';

const SUCCESSFUL_DURATION_DEFAULT = 1.5;
const SUCCESSFUL_DURATION_EXPORT_CSV = 3;
const ALL_INCIDENTS_EXPORT_ENTITY_ACTION = {
  [ALL_INCIDENTS_EXPORT_ENTITY_PERMISSION.BRAND]: ALL_INCIDENTS_TABLE_EXPORT_BRAND_PERMISSION,
  [ALL_INCIDENTS_EXPORT_ENTITY_PERMISSION.PIRACY]: ALL_INCIDENTS_TABLE_EXPORT_PIRACY_PERMISSION,
};

export const defaultPageable = () =>
  paginationToPageable({ sortProperty: 'assetName', order: 'ASC' });

export const allIncidentsFetchData = ({ filters, incidentLevelStatusData }) => {
  const { pageable = defaultPageable(), assetId } = filters;

  const incidentInfoFilters = getIncidentInfoFilters(
    filters,
    incidentLevelStatusData
  );

  return {
    type: CONNECTOR_REQUEST,
    meta: {
      requestType: ALL_INCIDENTS_FETCH_DATA,
      connectorMethod: IncidentInfoController.listWithFilters,
      sortedParameters: [assetId, incidentInfoFilters, pageable],
      expectedStatus: HttpStatus.OK,
      successfulMessage: '',
      errorMessage: translate('reporting.allIncidentsInternal.messages.error'),
    },
  };
};

export const allIncidentsClear = () => ({
  type: ALL_INCIDENTS_CLEAR,
});

export const allIncidentsClearData = () => ({
  type: ALL_INCIDENTS_DATA_CLEAR,
});

export const allIncidentsFiltersClearFilters = () => {
  return { type: ALL_INCIDENTS_FILTERS_CLEAR_FILTERS };
};

export const allIncidentsFiltersFetchNonEnforcementReasons = ({ domainId }) => {
  const enforcement = false;
  return {
    type: CONNECTOR_REQUEST,
    meta: {
      requestType: ALL_INCIDENTS_FILTERS_FETCH_NON_ENFORCEMENT_REASONS,
      connectorMethod: ReasonLiteController.list,
      sortedParameters: [domainId, enforcement],
      expectedStatus: HttpStatus.OK,
      successfulMessage: '',
      errorMessage: translate('reporting.allIncidentsInternal.messages.error'),
    },
  };
};

export const allIncidentsFiltersSetAsset = ({ assetId }) => {
  return {
    type: ALL_INCIDENTS_FILTERS_SET_ASSET,
    payload: { assetId },
  };
};

export const allIncidentsFiltersSetClosureDate = ({ closureDate }) => {
  return {
    type: ALL_INCIDENTS_FILTERS_SET_CLOSURE_DATE,
    payload: { closureDate },
  };
};

export const allIncidentsFiltersSetConfirmationDate = ({
  confirmationDate,
}) => {
  return {
    type: ALL_INCIDENTS_FILTERS_SET_CONFIRMATION_DATE,
    payload: { confirmationDate },
  };
};

export const allIncidentsFiltersSetDeindexation = ({ deindexation }) => ({
  type: ALL_INCIDENTS_FILTERS_SET_DEINDEXATION,
  payload: { deindexation },
});

export const allIncidentsFiltersSetDetectionDate = ({ detectionDate }) => {
  return {
    type: ALL_INCIDENTS_FILTERS_SET_DETECTION_DATE,
    payload: { detectionDate },
  };
};

export const allIncidentsFiltersSetDomains = ({ domainId }) => {
  return {
    type: ALL_INCIDENTS_FILTERS_SET_DOMAINS,
    payload: { domainId },
  };
};

export const allIncidentsFiltersSetEnforcementDate = ({ enforcementDate }) => {
  return {
    type: ALL_INCIDENTS_FILTERS_SET_ENFORCEMENT_DATE,
    payload: { enforcementDate },
  };
};

export const allIncidentsFiltersSetEnforcementStatus = ({
  enforcementStatus,
}) => {
  return {
    type: ALL_INCIDENTS_FILTERS_SET_ENFORCEMENT_STATUS,
    payload: {
      enforcementStatus,
    },
  };
};

export const allIncidentsFiltersSetEnforcementStatusGroup = ({
  enforcementStatusGroupId,
}) => {
  return {
    type: ALL_INCIDENTS_FILTERS_SET_ENFORCEMENT_STATUS_GROUP,
    payload: {
      enforcementStatusGroupId,
    },
  };
};

export const allIncidentsFiltersSetIncidentStatus = ({ incidentStatus }) => {
  return {
    type: ALL_INCIDENTS_FILTERS_SET_INCIDENT_STATUS,
    payload: { incidentStatus },
  };
};

export const allIncidentsFiltersSetIncidentStatusGroup = ({
  incidentStatusGroupId,
}) => {
  return {
    type: ALL_INCIDENTS_FILTERS_SET_INCIDENT_STATUS_GROUP,
    payload: { incidentStatusGroupId },
  };
};

export const allIncidentsFiltersSetPriorityId = ({ priorityId }) => ({
  type: ALL_INCIDENTS_FILTERS_SET_PRIORITY_ID,
  payload: { priorityId },
});

export const allIncidentsFiltersSetAppliedFilters = ({
  assetId,
  closureDateFrom,
  closureDateTo,
  confirmationDateFrom,
  confirmationDateTo,
  deindexation,
  detectionDateFrom,
  detectionDateTo,
  domainId,
  enforcementDateFrom,
  enforcementDateTo,
  enforcementStatus,
  enforcementStatusGroupId,
  incidentStatus,
  incidentStatusGroupId,
  linkUrl,
  reasonCode,
  priorityId,
  protectionType,
  url,
  watch,
  watching,
}) => {
  const filters = {
    assetId,
    closureDateFrom,
    closureDateTo,
    confirmationDateFrom,
    confirmationDateTo,
    deindexation,
    detectionDateFrom,
    detectionDateTo,
    domainId,
    enforcementDateFrom,
    enforcementDateTo,
    enforcementStatus,
    enforcementStatusGroupId,
    incidentStatus,
    incidentStatusGroupId,
    linkUrl,
    reasonCode,
    priorityId,
    protectionType,
    url,
    watch,
    watching,
  };
  return {
    type: ALL_INCIDENTS_FILTERS_SET_APPLIED_FILTERS,
    payload: { filters },
  };
};

export const allIncidentsFiltersSetProtectionType = ({ protectionType }) => {
  return {
    type: ALL_INCIDENTS_FILTERS_SET_PROTECTION_TYPE,
    payload: { protectionType },
  };
};

export const allIncidentsFiltersSetReasonCodes = ({ reasonCode }) => ({
  type: ALL_INCIDENTS_FILTERS_SET_REASON_CODES,
  payload: { reasonCode },
});

export const allIncidentsFiltersSetSearchLinkUrl = ({ linkUrl }) => ({
  type: ALL_INCIDENTS_FILTERS_SET_SEARCH_LINK_URL,
  payload: { linkUrl },
});

export const allIncidentsFiltersSetSearchUrl = ({ url }) => {
  return {
    type: ALL_INCIDENTS_FILTERS_SET_SEARCH_TERM,
    payload: { url },
  };
};

export const allIncidentsFiltersFetchAssets = ({ reportType }) => {
  const full = false;

  const assetFilter =
    reportType === 'all-incidents'
      ? AssetFilterEnum.ALL
      : AssetFilterEnum.ONLY_REGULAR_ASSETS;
  const assetsRequestFilter = new AssetsRequestFilter();
  assetsRequestFilter.all = true;
  assetsRequestFilter.assetFilter = assetFilter;

  return {
    type: CONNECTOR_REQUEST,
    meta: {
      requestType: ALL_INCIDENTS_FILTERS_FETCH_ASSETS,
      connectorMethod: AssetController.list,
      sortedParameters: [full, undefined, assetsRequestFilter],
      expectedStatus: HttpStatus.OK,
      successfulMessage: '',
      errorMessage: translate('reporting.allIncidentsInternal.messages.error'),
    },
  };
};

export const allIncidentsFiltersClearDomains = () => {
  return {
    type: ALL_INCIDENTS_FILTERS_CLEAR_DOMAINS,
  };
};

export const allIncidentsFiltersFetchDomains = ({ url }) => {
  return {
    type: CONNECTOR_REQUEST,
    meta: {
      requestType: ALL_INCIDENTS_FILTERS_FETCH_DOMAINS,
      connectorMethod: ContractDomainController.listEffective,
      sortedParameters: [url, DomainTypeEnum.DETECTION_DOMAIN],
      expectedStatus: HttpStatus.OK,
      successfulMessage: '',
      errorMessage: translate('reporting.allIncidentsInternal.messages.error'),
    },
  };
};

export const allIncidentsFiltersFetchIncidentsStatus = () => {
  const contract = null;
  return {
    type: CONNECTOR_REQUEST,
    meta: {
      requestType: ALL_INCIDENTS_FILTERS_FETCH_INCIDENTS_STATUS,
      connectorMethod: IncidentStatusGroupController.getIncidentStatusGroups,
      sortedParameters: [contract],
      expectedStatus: HttpStatus.OK,
      successfulMessage: '',
      errorMessage: translate('reporting.allIncidentsInternal.messages.error'),
    },
  };
};

export const allIncidentsFiltersFetchLinksStatus = () => {
  return {
    type: CONNECTOR_REQUEST,
    meta: {
      requestType: ALL_INCIDENTS_FILTERS_FETCH_LINKS_STATUS,
      connectorMethod: EnforcementStatusGroupController.list,
      sortedParameters: [],
      expectedStatus: HttpStatus.OK,
      successfulMessage: '',
      errorMessage: translate('reporting.allIncidentsInternal.messages.error'),
    },
  };
};

export const allIncidentsFiltersFetchProtectionTypes = () => {
  const full = true;
  return {
    type: CONNECTOR_REQUEST,
    meta: {
      requestType: ALL_INCIDENTS_FILTERS_FETCH_PROTECTION_TYPES,
      connectorMethod: ContractController.get,
      sortedParameters: [full],
      expectedStatus: HttpStatus.OK,
      successfulMessage: '',
      errorMessage: translate('reporting.allIncidentsInternal.messages.error'),
    },
  };
};

export const allIncidentsFiltersSetBookmark = ({ value }) => {
  return {
    type: ALL_INCIDENTS_FILTERS_SET_BOOKMARK,
    payload: { value },
  };
};

export const allIncidentSetBookmarkStatus = ({
  assetId,
  detectionLinkId,
  incidentId,
  watching,
}) => ({
  type: CONNECTOR_REQUEST,
  meta: {
    requestType: ALL_INCIDENTS_TABLE_ACTION,
    connectorMethod: watching
      ? DetectionLinkWatcherController.createWatch
      : DetectionLinkWatcherController.deleteWatch,
    sortedParameters: [assetId, detectionLinkId],
    expectedStatus: HttpStatus.NO_CONTENT,
    errorMessage: translate('allIncidents.bookmarkAction.error'),
    successfulMessage: translate('allIncidents.bookmarkAction.success'),
    successfulDuration: SUCCESSFUL_DURATION_DEFAULT,
  },
  payload: {
    incidentId,
    isBookmark: true,
  },
});

export const allIncidentsExportEnforcementData = ({
  filters,
  incidentLevelStatusData = {},
}) => {
  const { assetId } = filters;
  const incidentInfoFilters = getIncidentInfoFilters(
    filters,
    incidentLevelStatusData
  );

  return {
    type: CONNECTOR_REQUEST,
    meta: {
      requestType: ALL_INCIDENTS_TABLE_EXPORT_ACTION,
      connectorMethod:
        IncidentInfoExportController.createEnforcementDataInfoWithFilters,
      sortedParameters: [assetId, incidentInfoFilters],
      expectedStatus: HttpStatus.NO_CONTENT,
      successfulDuration: SUCCESSFUL_DURATION_EXPORT_CSV,
      errorMessage: translate('allIncidents.enforcementDataToCSV.error'),
      successfulMessage: translate('allIncidents.enforcementDataToCSV.success'),
    },
  };
};

export const allIncidentsFetchExportEntityPermission = ({ entity }) => ({
  type: CONNECTOR_REQUEST,
  meta: {
    requestType: ALL_INCIDENTS_EXPORT_ENTITY_ACTION[entity],
    connectorMethod: AuthorizationController.hasPermission,
    sortedParameters: [entity, OperationsEnum.READ, SuiteEnum.IPR],
    expectedStatus: HttpStatus.NO_CONTENT,
    errorMessage: translate('reporting.allIncidentsInternal.messages.error'),
  },
});

export const allIncidentsExportBrandData = ({
  filters,
  incidentLevelStatusData,
}) => {
  const { assetId } = filters;
  const incidentInfoFilters = getIncidentInfoFilters(
    filters,
    incidentLevelStatusData
  );

  return {
    type: CONNECTOR_REQUEST,
    meta: {
      requestType: ALL_INCIDENTS_TABLE_EXPORT_BRAND_DATA_ACTION,
      connectorMethod: IncidentInfoExportController.createBrandInfo,
      sortedParameters: [assetId, incidentInfoFilters],
      expectedStatus: HttpStatus.NO_CONTENT,
      errorMessage: translate('allIncidents.exportBrandDataToCSV.error'),
      successfulMessage: translate('allIncidents.exportBrandDataToCSV.success'),
    },
  };
};

export const allIncidentsExportPiracyData = ({
  filters,
  incidentLevelStatusData,
}) => {
  const { assetId } = filters;
  const incidentInfoFilters = getIncidentInfoFilters(
    filters,
    incidentLevelStatusData
  );

  return {
    type: CONNECTOR_REQUEST,
    meta: {
      requestType: ALL_INCIDENTS_TABLE_EXPORT_PIRACY_DATA_ACTION,
      connectorMethod: IncidentInfoExportController.createPiracyInfo,
      sortedParameters: [assetId, incidentInfoFilters],
      expectedStatus: HttpStatus.NO_CONTENT,
      errorMessage: translate('allIncidents.exportPiracyDataToCSV.error'),
      successfulMessage: translate(
        'allIncidents.exportPiracyDataToCSV.success'
      ),
    },
  };
};

export const allIncidentsTableFetchDeindexationByLink = ({
  assetId,
  incidentId,
  linkId,
  incidentType,
}) => ({
  type: CONNECTOR_REQUEST,
  meta: {
    requestType: ALL_INCIDENTS_TABLE_FETCH_DEINDEXATION_BY_LINK,
    connectorMethod: DeindexationController.list,
    sortedParameters: [assetId, linkId],
    expectedStatus: HttpStatus.OK,
    errorMessage: translate('allIncidents.getDeindexation.error'),
  },
  payload: {
    incidentId,
    incidentType,
  },
});
